import React from 'react';
import { Grid, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import TodayIcon from '@mui/icons-material/Today';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { StatCard } from './StatCard';

interface EngagementDashboardProps {
    data: any;
}

const formatTime = (seconds: number): string => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

export const EngagementDashboard: React.FC<EngagementDashboardProps> = ({ data }) => {
    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">Keine Daten verfügbar</Typography>
            </Box>
        );
    }

    const { metrics, streakStats, topPerformers } = data;
    const showCompany = (topPerformers || []).some((performer: any) => performer.user_company);

    return (
        <Box sx={{ px: 2 }}>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Aktive Nutzer"
                        value={metrics?.active_users || 0}
                        info="Personen, die im gewählten Zeitraum aktiv waren: eingeschrieben, gelernt oder abgeschlossen."
                        icon={<PeopleIcon sx={{ fontSize: 48 }} />}
                        color="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Wöchentlich Aktiv"
                        value={metrics?.weekly_active_users || 0}
                        icon={<TodayIcon sx={{ fontSize: 48 }} />}
                        color="info"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Täglich Aktiv"
                        value={metrics?.daily_active_users || 0}
                        icon={<TodayIcon sx={{ fontSize: 48 }} />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Ø Session"
                        value={formatTime(metrics?.avg_session_seconds || 0)}
                        icon={<LocalFireDepartmentIcon sx={{ fontSize: 48 }} />}
                        color="warning"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Streak Statistics */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LocalFireDepartmentIcon sx={{ color: '#ff9800', mr: 1 }} />
                            <Typography variant="h6">
                                Serien-Statistiken
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            Aktuell laufende Serien (letzte Aktivität heute oder gestern), unabhängig vom gewählten Zeitraum.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Nutzer mit laufender Serie</TableCell>
                                        <TableCell align="right">
                                            <strong>{streakStats?.total_users_with_streak || 0}</strong>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Ø aktuelle Serie</TableCell>
                                        <TableCell align="right">
                                            <strong>{parseFloat(streakStats?.avg_current_streak || 0).toFixed(1)} Tage</strong>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Max. aktuelle Serie</TableCell>
                                        <TableCell align="right">
                                            <strong>{streakStats?.max_current_streak || 0} Tage</strong>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Ø längste Serie</TableCell>
                                        <TableCell align="right">
                                            <strong>{parseFloat(streakStats?.avg_longest_streak || 0).toFixed(1)} Tage</strong>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Max. längste Serie</TableCell>
                                        <TableCell align="right">
                                            <strong>{streakStats?.max_longest_streak || 0} Tage</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Top Performers */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EmojiEventsIcon sx={{ color: '#ffc107', mr: 1 }} />
                            <Typography variant="h6">
                                Top Performer
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Name</TableCell>
                                        {showCompany && <TableCell>Unternehmen</TableCell>}
                                        <TableCell align="right">Abschlüsse</TableCell>
                                        <TableCell align="right">Ø Fortschritt</TableCell>
                                        <TableCell align="right">Lernzeit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(topPerformers || []).slice(0, 5).map((performer: any, index: number) => (
                                        <TableRow key={performer.user_id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{performer.user_name || `Nutzer #${performer.user_id}`}</TableCell>
                                            {showCompany && <TableCell>{performer.user_company || '-'}</TableCell>}
                                            <TableCell align="right">{performer.completions || 0}</TableCell>
                                            <TableCell align="right">{parseFloat(performer.avg_progress || 0).toFixed(1)}%</TableCell>
                                            <TableCell align="right">{formatTime(performer.total_time_seconds || 0)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!topPerformers || topPerformers.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={showCompany ? 6 : 5} align="center">
                                                <Typography color="textSecondary">Keine Daten</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default EngagementDashboard;
